import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { useLanguage } from '../context/LanguageContext'
import SEO from '../components/SEO'
import PortfolioCard from '../components/PortfolioCard'
import ProjectModal from '../components/ProjectModal'
import { websiteProjects, categories } from '../data/portfolioData'

const Portfolio = () => {
  const { t, language } = useLanguage()
  const [activeFilter, setActiveFilter] = useState('all')
  const [selectedProject, setSelectedProject] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const filteredProjects = activeFilter === 'all' 
    ? websiteProjects 
    : websiteProjects.filter(p => p.category === activeFilter)
  
  const handleViewDetails = (project) => {
    setSelectedProject(project)
    setIsModalOpen(true)
  }
  
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setTimeout(() => setSelectedProject(null), 300)
  }

  return (
    <>
      <SEO 
        title={t('seo.portfolio.title')}
        description={t('seo.portfolio.description')}
        keywords={t('seo.portfolio.keywords')}
      />
      <div className="mt-20">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark relative overflow-hidden">
        <motion.div
          animate={{ rotate: [0, 360], scale: [1, 1.2, 1], opacity: [0.03, 0.08, 0.03] }}
          transition={{ duration: 30, repeat: Infinity }}
          className="absolute top-0 left-0 w-96 h-96 bg-gradient-gold rounded-full blur-3xl"
        />
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="section-title mb-6">
              {t('portfolioPage.title')} <span className="text-gradient-gold">{t('portfolioPage.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-beige-200 max-w-3xl mx-auto mb-8">
              {t('portfolioPage.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-beige-300">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-gold-400/20">
                <span className="text-2xl">🎨</span>
                <span className="text-sm font-semibold">{websiteProjects.length}+ {language === 'sq' ? 'Projekte' : 'Projects'}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-gold-400/20">
                <span className="text-2xl">⭐</span>
                <span className="text-sm font-semibold">{websiteProjects.filter(p => p.featured).length} Featured</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-gold-400/20">
                <span className="text-2xl">🚀</span>
                <span className="text-sm font-semibold">{websiteProjects.filter(p => p.status === 'live').length} Live</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark relative overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -90, 0],
            opacity: [0.05, 0.15, 0.05]
          }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-bronze rounded-full blur-3xl"
        />
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Filter Buttons */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap justify-center gap-3 mb-16"
          >
            {categories.map((cat) => (
              <motion.button
                key={cat.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveFilter(cat.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  activeFilter === cat.id
                    ? 'bg-gradient-gold text-white shadow-lg shadow-gold-400/30'
                    : 'bg-bronze-900/50 text-beige-200 hover:bg-bronze-900 border border-gold-400/20'
                }`}
              >
                {cat.label[language]}
              </motion.button>
            ))}
          </motion.div>

          {/* Projects Grid */}
          <AnimatePresence mode="wait">
            <motion.div 
              key={activeFilter}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProjects.map((project) => (
                <PortfolioCard
                  key={project.id}
                  project={project}
                  onViewDetails={() => handleViewDetails(project)}
                  language={language}
                />
              ))}
            </motion.div>
          </AnimatePresence>
          
          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gold-400 mb-2">
                {language === 'sq' ? 'Asnjë projekt i gjetur' : 'No projects found'}
              </h3>
              <p className="text-beige-300">
                {language === 'sq' ? 'Provo një kategori tjetër' : 'Try another category'}
              </p>
            </motion.div>
          )}
        </div>
      </section>
      
      {/* Project Modal */}
      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        language={language}
      />
    </div>
    </>
  )
}

export default Portfolio
